import { toDateInputString, toTimeString } from "./normalizers";

export const PICKUP_QUICK_OFFSETS_MINUTES = [10, 15, 20, 30] as const;

type Nullable<T> = T | null | undefined;


export type PickupQuickCandidate = {
    key: string;
    offsetMinutes: number;
    dateObj: Date;
    date: string;
    time: string;
};

type BuildPickupQuickDateCandidatesArgs = {
    baseDateObj: Nullable<Date>;
    offsetsMinutes?: readonly number[];
};

type BuildPickupQuickOptionsArgs<T> = {
    baseDateObj: Nullable<Date>;
    offsetsMinutes?: readonly number[];
    filterOption?: (option: PickupQuickCandidate) => boolean;
    mapOption?: (option: PickupQuickCandidate) => Nullable<T>;
};

type PrependUniquePickupQuickBaseOptionArgs<T extends { date?: string; time?: string }> = {
    baseOption?: Nullable<T>;
    options?: Nullable<T[]>;
};

const isNonNullable = <T,>(value: Nullable<T>): value is T => value != null;

const buildPickupQuickDateCandidates = ({
    baseDateObj,
    offsetsMinutes = PICKUP_QUICK_OFFSETS_MINUTES,
}: BuildPickupQuickDateCandidatesArgs): PickupQuickCandidate[] => {
    if (!(baseDateObj instanceof Date) || Number.isNaN(baseDateObj.getTime())) return [];

    return offsetsMinutes
        .map((offsetMinutes) => {
            const dateObj = new Date(baseDateObj.getTime() + (offsetMinutes * 60 * 1000));
            if (Number.isNaN(dateObj.getTime())) return null;

            const date = toDateInputString(dateObj);
            const time = toTimeString(dateObj);
            if (!date || !time) return null;

            return {
                key: `${offsetMinutes}-${date}-${time}`,
                offsetMinutes,
                dateObj,
                date,
                time,
            };
        })
        .filter(isNonNullable);
};

export const buildPickupQuickOptions = <T = PickupQuickCandidate>({
    baseDateObj,
    offsetsMinutes = PICKUP_QUICK_OFFSETS_MINUTES,
    filterOption,
    mapOption,
}: BuildPickupQuickOptionsArgs<T>): T[] => {
    const candidates = buildPickupQuickDateCandidates({
        baseDateObj,
        offsetsMinutes,
    });

    return candidates
        .filter((option) => (
            typeof filterOption === "function"
                ? filterOption(option)
                : true
        ))
        .map((option) => (
            typeof mapOption === "function"
                ? mapOption(option)
                : (option as T)
        ))
        .filter(isNonNullable);
};

export const prependUniquePickupQuickBaseOption = <T extends { date?: string; time?: string }>({
    baseOption = null,
    options = [],
}: PrependUniquePickupQuickBaseOptionArgs<T>): T[] => {
    if (!baseOption) return options || [];

    const normalizedOptions = options || [];
    const hasDuplicate = normalizedOptions.some((option) => (
        option?.date === baseOption?.date &&
        option?.time === baseOption?.time
    ));

    return hasDuplicate
        ? normalizedOptions
        : [baseOption, ...normalizedOptions];
};
